// ProductEditDrawers — the LOCAL product's per-facet edit slideovers (the
// architect's one-set-at-a-time ruling; the asset page's Edit-menu grammar):
// Identity & specs · Capabilities & interfaces. Canonical rows never reach
// these (read-only on the instance — the peek offers no door). Each facet
// field replaces whole on save (the server normalizes: registry order · the
// absence law). The edit rides the row stamp (ADR-084): each drawer seeds from
// the product as it is at the open (the peek's detail read), the save carries
// the product's stamp, and a stale save shows the line with Load the latest.

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import { ASSET_CATEGORY_DESCRIPTORS, isConnectivityNa, isNetworkCapable } from '@ems-app/schemas/assets/categories';
import type { UpdateAssetModelRequest } from '@ems-app/schemas/catalog/requests';
import type { AssetModelProjectItem } from '@ems-app/schemas/catalog/responses';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { assetModelKeys, fetchAssetModel, useUpdateAssetModel } from '../../hooks/useAssetModels.js';
import {
  buildDeviceHalf,
  buildSpecBody,
  EMPTY_PRODUCT_FORM,
  ProductDeviceFields,
  ProductFormSchema,
  ProductSpecFields,
  type ProductFormValues,
} from './ProductCreateWizard.js';

/** Hydrate the shared product form model from a wire item. */
export function modelToFormValues(m: AssetModelProjectItem): ProductFormValues {
  const cat = m.category as AssetCategory;
  const effectiveNetworked = isNetworkCapable(cat, m.is_network_device);
  return {
    ...EMPTY_PRODUCT_FORM,
    category: m.category,
    name: m.name,
    manufacturer: m.manufacturer ?? '',
    series: m.series ?? '',
    partNumber: m.part_number ?? '',
    description: m.description ?? '',
    specPowerKw: m.power_kw,
    specCapacityKva: m.capacity_kva,
    specCapacityAh: m.capacity_ah,
    specVoltageV: m.voltage_v,
    specWattPeakW: m.watt_peak_w,
    specCoolingKw: m.cooling_kw,
    specTankL: m.tank_l,
    specBatteryChem: m.battery_chemistry,
    specBatteryTech: m.battery_technology,
    warrantyMonths: m.warranty_months,
    networked: effectiveNetworked,
    fronts: m.interfaces.fronts === true,
    capabilities: [...m.capabilities],
    sims: m.interfaces.sims ?? null,
    esim: m.interfaces.esim === true,
    ethernetPorts: m.interfaces.ethernet?.ports ?? null,
    ethernetWan: m.interfaces.ethernet?.wan ?? null,
    rs232: m.interfaces.serial?.rs232 ?? null,
    rs485: m.interfaces.serial?.rs485 ?? null,
  };
}

/** The inherit-vs-override switch, normalized back to NULL on the charter's
 *  three-state default (an na category has no switch and never overrides). */
function networkedOverride(v: ProductFormValues): boolean | null {
  const cat = v.category as AssetCategory;
  if (isConnectivityNa(cat)) return null;
  return v.networked === (ASSET_CATEGORY_DESCRIPTORS[cat].connectivity === 'on') ? null : v.networked;
}

export function ProductIdentityDrawer({
  projectId,
  model,
  open,
  onClose,
}: {
  projectId: string;
  model: AssetModelProjectItem;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('catalog');
  const qc = useQueryClient();
  const update = useUpdateAssetModel(projectId, model.id);
  const shell = useFormShell<ProductFormValues, AssetModelProjectItem>({
    schema: ProductFormSchema,
    row: model,
    values: (r) => modelToFormValues(r ?? model),
    // The fresh product: a quiet read (a product gone meanwhile is said in the form), into the peek's entry once it answers.
    reload: () => readLatest(qc, assetModelKeys.detail(projectId, model.id), () => fetchAssetModel(projectId, model.id, true)),
    onSubmit: async (v, { stamp }) => {
      const specs = buildSpecBody(v);
      // The facet whole, with the stamp the product carried as the drawer opened.
      const body: UpdateAssetModelRequest = {
        row_version: stamp,
        name: v.name.trim(),
        manufacturer: v.manufacturer.trim() || null,
        series: v.series.trim() || null,
        part_number: v.partNumber.trim() || null,
        description: v.description.trim() || null,
        // every legal spec travels (value or null — clearing is a real edit)
        power_kw: specs.power_kw ?? null,
        capacity_kva: specs.capacity_kva ?? null,
        capacity_ah: specs.capacity_ah ?? null,
        voltage_v: specs.voltage_v ?? null,
        watt_peak_w: specs.watt_peak_w ?? null,
        cooling_kw: specs.cooling_kw ?? null,
        tank_l: specs.tank_l ?? null,
        battery_chemistry: specs.battery_chemistry ?? null,
        battery_technology: specs.battery_technology ?? null,
        warranty_months: v.warrantyMonths && v.warrantyMonths > 0 ? v.warrantyMonths : null,
        is_network_device: networkedOverride(v),
      };
      await update.mutateAsync(body);
    },
    onSuccess: onClose,
  });
  const { form } = shell;
  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={t('edit.identityTitle')}
      submitLabel={t('edit.save')}
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="pe-name" required>{t('wizard.fields.name')}</Label>
          <Input id="pe-name" dir="ltr" {...form.register('name')} error={form.formState.errors.name ? ' ' : undefined} />
        </div>
        <div>
          <Label htmlFor="pe-maker">{t('wizard.fields.manufacturer')}</Label>
          <Input id="pe-maker" dir="ltr" {...form.register('manufacturer')} />
        </div>
        <div>
          <Label htmlFor="pe-series">{t('wizard.fields.series')}</Label>
          <Input id="pe-series" dir="ltr" {...form.register('series')} />
        </div>
        <div>
          <Label htmlFor="pe-pn">{t('wizard.fields.partNumber')}</Label>
          <Input id="pe-pn" dir="ltr" className="font-mono" {...form.register('partNumber')} />
        </div>
        <div>
          <Label htmlFor="pe-desc">{t('wizard.fields.description')}</Label>
          <Textarea id="pe-desc" rows={3} {...form.register('description')} />
        </div>
        <ProductSpecFields form={form} />
        {/* structurally absent on a connectivity-na category (ADR-067 rev) */}
        {!isConnectivityNa(model.category as AssetCategory) && (
          <div className="border-t border-[var(--color-border)] pt-3">
            <Toggle
              checked={form.watch('networked')}
              onChange={(on) => form.setValue('networked', on, { shouldDirty: true })}
              label={t('wizard.fields.networked')}
            />
          </div>
        )}
      </div>
    </DrawerForm>
  );
}

export function ProductDeviceDrawer({
  projectId,
  model,
  open,
  onClose,
}: {
  projectId: string;
  model: AssetModelProjectItem;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('catalog');
  const qc = useQueryClient();
  const update = useUpdateAssetModel(projectId, model.id);
  const shell = useFormShell<ProductFormValues, AssetModelProjectItem>({
    schema: ProductFormSchema,
    row: model,
    values: (r) => ({ ...modelToFormValues(r ?? model), networked: true }),
    reload: () => readLatest(qc, assetModelKeys.detail(projectId, model.id), () => fetchAssetModel(projectId, model.id, true)),
    onSubmit: async (v, { stamp }) => {
      const half = buildDeviceHalf({ ...v, networked: true });
      await update.mutateAsync({ row_version: stamp, capabilities: half.capabilities, interfaces: half.interfaces });
    },
    onSuccess: onClose,
  });
  const { form } = shell;
  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={t('edit.deviceTitle')}
      submitLabel={t('edit.save')}
    >
      <div className="space-y-4">
        <Checkbox
          checked={form.watch('fronts')}
          onChange={(on) => form.setValue('fronts', on, { shouldDirty: true })}
          label={t('wizard.fields.fronts')}
          description={t('wizard.fields.frontsHint')}
        />
        <ProductDeviceFields form={form} />
      </div>
    </DrawerForm>
  );
}
